import { useState, useCallback } from 'react'
import { internalApiClient } from '@/services/apiClient'
import { toastService } from '@/services/toastService'
import { useTranslation } from '@/hooks/useTranslation'


export interface ProjectPosition {
  id: string
  positionNumber: number
  sequenceNumber: number
  buildingFloor: string | null
  description: string
  articleDescription: string | null
  assetModelId: string
  width: number | null
  height: number | null
  notes: string | null
  projectId: string
  qrTagId: string | null
  tenantId: string
  createdAt: string
  updatedAt: string
  assetModel?: {
    id: string
    name: string
    type: string
    brand: string
    model: string
    description?: string | null
  }
  qrTag?: {
    id: string
    serialNo: string
    sequence: number
    year: number
    clientCode: string
    status: string
  }
}

export interface UseInstallerProjectPositionsResult {
  positions: ProjectPosition[]
  loading: boolean
  error: string | null
  linkingError: string | null
  isLinking: boolean
  isUnlinking: boolean
  fetchPositionsByProject: (projectId: string) => Promise<void>
  linkQrTag: (positionId: string, qrTagId: string) => Promise<boolean>
  unlinkQrTag: (positionId: string) => Promise<boolean>
  updateLocalPosition: (positionId: string, updates: Partial<ProjectPosition>) => void
}

export function useInstallerProjectPositions(): UseInstallerProjectPositionsResult {
  const { t } = useTranslation()
  const [positions, setPositions] = useState<ProjectPosition[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [linkingError, setLinkingError] = useState<string | null>(null)
  const [isLinking, setIsLinking] = useState(false)
  const [isUnlinking, setIsUnlinking] = useState(false)

  const fetchPositionsByProject = useCallback(async (projectId: string) => {
    setLoading(true)
    setError(null)

    try {
      const response = await internalApiClient.get<ProjectPosition[]>(
        `/installer-project-positions/by-project/${projectId}`
      )
      setPositions(response.data)
    } catch (err) {
      console.error('Error fetching project positions:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch project positions'
      setError(errorMessage)
      toastService.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  const linkQrTag = useCallback(async (positionId: string, qrTagId: string): Promise<boolean> => {
    setIsLinking(true)
    setLinkingError(null)

    try {
      const response = await internalApiClient.patch<ProjectPosition>(
        `/installer-project-positions/${positionId}/link-qrtag/${qrTagId}`
      )

      // Update local state with the response
      setPositions(prev =>
        prev.map(pos => pos.id === positionId ? response.data : pos)
      )

      toastService.success(t('LINKING_QR_TAG_LINKED_SUCCESS'))
      return true
    } catch (err) {
      console.error('Failed to link QR tag:', err)
      const errorMessage = err instanceof Error ? err.message : t('LINKING_FAILED_TO_LINK_QR_TAG')
      setLinkingError(errorMessage)
      toastService.error(errorMessage)
      return false
    } finally {
      setIsLinking(false)
    }
  }, [t])

  const unlinkQrTag = useCallback(async (positionId: string): Promise<boolean> => {
    setIsUnlinking(true)
    setLinkingError(null)

    try {
      const response = await internalApiClient.patch<ProjectPosition>(
        `/installer-project-positions/${positionId}/unlink-qrtag`
      )

      // Update local state with the response
      setPositions(prev =>
        prev.map(pos => pos.id === positionId ? response.data : pos)
      )

      toastService.success(t('LINKING_QR_TAG_UNLINKED_SUCCESS'))
      return true
    } catch (err) {
      console.error('Failed to unlink QR tag:', err)
      const errorMessage = err instanceof Error ? err.message : t('LINKING_FAILED_TO_UNLINK_QR_TAG')
      setLinkingError(errorMessage)
      toastService.error(errorMessage)
      return false
    } finally {
      setIsUnlinking(false)
    }
  }, [t])

  const updateLocalPosition = useCallback((positionId: string, updates: Partial<ProjectPosition>) => {
    setPositions(prev =>
      prev.map(pos => pos.id === positionId ? { ...pos, ...updates } : pos)
    )
  }, [])

  return {
    positions,
    loading,
    error,
    linkingError,
    isLinking,
    isUnlinking,
    fetchPositionsByProject,
    linkQrTag,
    unlinkQrTag,
    updateLocalPosition
  }
}